import { AnimationAction, AnimationClip, AnimationMixer, LoopRepeat, Mesh, Object3D, Vector3 } from "three";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { Ticker } from "pixi.js";
import { AnimationParsingUtils } from "../../../../../../three/AnimationParsingUtils";
import { SmokeParticle } from "../particles/tractor/SmokeParticle";
import { BatchedRenderer } from "three.quarks";
import { WheelEarthClodsParticle } from "../particles/tractor/WheelEarthClodsParticle";
import { TractorTrailParticle } from "../particles/tractor/TractorTrailParticle";

export class Tractor extends Object3D {
    private tractor: Object3D;
    private readonly animationMixer: AnimationMixer;
    private moveAnimation: AnimationAction;
    private moveAnimationShaking: AnimationAction;
    private readonly trailerPoint: Object3D;
    private readonly pipePoint: Object3D;
    private smokeParticle: SmokeParticle;
    private readonly leftBigWheelPoint: Object3D;
    private earthClodsParticle: WheelEarthClodsParticle;
    private leftTrailParticle: TractorTrailParticle;
    private readonly rightBigWheelPoint: Object3D;
    private rightTrailParticle: TractorTrailParticle;
    constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly particlesRenderer: BatchedRenderer,
        private readonly vegetables: Object3D[],
        private readonly startPosition: Vector3,
        private readonly endPosition: Vector3,
    ) {
        super();
        this.tractor = clone(this.gameResources.assets_game_Tractor_glb.scene.children[0]);
        this.trailerPoint = this.tractor.getObjectByName("TargetVegetables");
        this.pipePoint = this.tractor.getObjectByName("SmokeStart");
        this.leftBigWheelPoint = this.tractor.children[1].getObjectByName("StartDust_L");
        this.rightBigWheelPoint = this.tractor.children[1].getObjectByName("StartDust_R");
        this.tractor.position.copy(startPosition);
        this.tractor.rotation.y = Math.PI / 2;
        this.animationMixer = new AnimationMixer(this.tractor.children[0]);
        this.parseTractorAnimations(this.gameResources.assets_game_Tractor_glb.animations);
        this.moveAnimationShaking.play();
        this.animationMixer.timeScale = 0;
        this.add(this.tractor);
        Ticker.shared.add(this.onTick, this);
    }

    public animate(): Promise<void> {
        this.smokeParticle = new SmokeParticle(this.gameResources, this.particlesRenderer);
        this.pipePoint.add(this.smokeParticle);
        this.earthClodsParticle = new WheelEarthClodsParticle(this.gameResources, this.particlesRenderer);
        this.leftBigWheelPoint.add(this.earthClodsParticle);
        this.leftTrailParticle = new TractorTrailParticle(this.gameResources, this.particlesRenderer);
        this.leftTrailParticle.rotateY(-Math.PI);
        this.leftBigWheelPoint.add(this.leftTrailParticle);
        this.rightTrailParticle = new TractorTrailParticle(this.gameResources, this.particlesRenderer);
        this.rightTrailParticle.rotateY(-Math.PI);
        this.rightBigWheelPoint.add(this.rightTrailParticle);

        this.animationMixer.timeScale = 1;

        const moveTo = (from: Vector3, to: Vector3, duration: number): Promise<void> => {
            return new Promise(resolve => {
                let elapsed = 0;
                const update = () => {
                    elapsed += Ticker.shared.deltaMS;
                    const progress = Math.min(elapsed / duration, 1);
                    this.tractor.position.lerpVectors(from, to, progress);

                    if (progress >= 1) {
                        Ticker.shared.remove(update, this);
                        resolve();
                    }
                };
                Ticker.shared.add(update, this);
            });
        };

        const wait = (duration: number): Promise<void> => {
            this.animationMixer.timeScale = 0;
            this.earthClodsParticle.stop();
            return new Promise(resolve => {
                let elapsed = 0;
                const update = () => {
                    elapsed += Ticker.shared.deltaMS;
                    if (elapsed >= duration) {
                        Ticker.shared.remove(update, this);
                        this.animationMixer.timeScale = 1;
                        this.earthClodsParticle.play();
                        resolve();
                    }
                };
                Ticker.shared.add(update, this);
            });
        };


        const jumpVegetable = (veg: Object3D, jumpHeight: number = 10, durationMS: number = 2000): Promise<void> => {
            return new Promise(resolve => {
                const start = veg.getWorldPosition(new Vector3()).clone();
                const end = this.trailerPoint.getWorldPosition(new Vector3());
                const mid = start.clone().lerp(end, 0.5);
                mid.y += jumpHeight;
                let elapsed = 0;
                const animate = (_dt: number) => {
                    elapsed += Ticker.shared.deltaMS;
                    let progress = Math.min(elapsed / durationMS, 1);

                    const pos = new Vector3()
                        .addScaledVector(start, (1 - progress) * (1 - progress))
                        .addScaledVector(mid, 2 * (1 - progress) * progress)
                        .addScaledVector(end, progress * progress);

                    veg.position.copy(pos);

                    if (progress >= 1) {
                        Ticker.shared.remove(animate, this);
                        veg.visible = false;
                        resolve();
                    }
                };

                Ticker.shared.add(animate, this);
            });
        }

        const jumpAll = async (): Promise<void[]> => {
            return Promise.all(this.vegetables.map(veg => jumpVegetable(veg, 10, 1000)));
        }

        return new Promise(async resolve => {
            // Phase 1: move into center
            const centerPos = this.tractor.position.clone();
            centerPos.x = 3;
            await moveTo(this.startPosition.clone(), centerPos, 2000);

            // Phase 2: stay in center
            const waitPromise = wait(1000);
            const jumpPromise = jumpAll();
            await Promise.all([waitPromise, jumpPromise]);
            // Phase 3: move out to endPosition
            await moveTo(centerPos, this.endPosition.clone(), 2000);

            resolve();
        });
    }

    private parseTractorAnimations(animations: AnimationClip[]): void {
        this.moveAnimation = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Movement");
        if (this.moveAnimation == null) {
            throw new Error(`Tractor has no Movement animations configured. Check model you importing.`);
        }
        this.moveAnimation.setLoop(LoopRepeat, Infinity);
        this.moveAnimationShaking = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Movement_V2");
        if (this.moveAnimationShaking == null) {
            throw new Error(`Mole has no Movement_V2 animations configured. Check model you importing.`);
        }
        this.moveAnimationShaking.setLoop(LoopRepeat, Infinity);
    }

    private onTick(dt: number): void {
        this.animationMixer.update(dt / 50);
    }

    public destroy(): void {
        Ticker.shared.remove(this.onTick, this);

        if (this.moveAnimation) {
            this.moveAnimation.stop();
        }
        if (this.moveAnimationShaking) {
            this.moveAnimationShaking.stop();
        }
        this.animationMixer.stopAllAction();

        this.tractor.traverse(obj => {
            const mesh = obj as Mesh;
            if (mesh.isMesh) {
                if (mesh.geometry) {
                    mesh.geometry.dispose();
                }
                if (mesh.material) {
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach(mat => mat.dispose());
                    } else {
                        mesh.material.dispose();
                    }
                }
            }
        });

        this.remove(this.tractor);
    }
}